import { isDevMode } from '@angular/core';
import type { Routes } from '@angular/router';
import { AuthGuardAccess } from './common/enums/auth-guard-access';
import { authGuard } from './common/guards/auth.guard';
import { joinGuard } from './common/guards/join.guard';
import { registerEmailGuard } from './common/guards/register-email.guard';
import { resetPasswordGuard } from './common/guards/reset-password.guard';
import { registerDocumentsResolver } from './common/resolvers/register-documents.resolver';

export const AUTH_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./common/components/auth-interface/auth-interface.component').then(
        (m) => m.AuthInterfaceComponent,
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'login',
      },
      {
        path: 'login',
        loadComponent: () =>
          import('./login/login.component').then((m) => m.LoginComponent),
        canActivate: [authGuard(AuthGuardAccess.PUBLIC)],
      },
      ...(isDevMode()
        ? [
            {
              path: 'register',
              children: [
                {
                  path: '',
                  pathMatch: 'full',
                  loadComponent: () =>
                    import('./register/register.component').then(
                      (m) => m.RegisterComponent,
                    ),
                  canActivate: [authGuard(AuthGuardAccess.PUBLIC)],
                },
                {
                  path: 'email',
                  loadComponent: () =>
                    import('./register/register-email/register-email.component').then(
                      (m) => m.RegisterEmailComponent,
                    ),
                  canActivate: [
                    authGuard(AuthGuardAccess.PUBLIC),
                    registerEmailGuard,
                  ],
                },
                {
                  path: 'company',
                  loadComponent: () =>
                    import('./register-company/register-company.component').then(
                      (m) => m.RegisterCompanyComponent,
                    ),
                  canActivate: [authGuard(AuthGuardAccess.REGISTER_COMPANY)],
                },
                {
                  path: 'documents',
                  children: [
                    {
                      path: '',
                      pathMatch: 'full',
                      loadComponent: () =>
                        import('./register-documents/register-documents.component').then(
                          (m) => m.RegisterDocumentsComponent,
                        ),
                      canActivate: [
                        authGuard(AuthGuardAccess.REGISTER_DOCUMENTS),
                      ],
                      resolve: { documents: registerDocumentsResolver },
                    },
                    {
                      path: 'success',
                      loadComponent: () =>
                        import('./success/success.component').then(
                          (m) => m.SuccessComponent,
                        ),
                      canActivate: [authGuard(AuthGuardAccess.PUBLIC)],
                    },
                  ],
                },
              ] satisfies Routes,
            },
          ]
        : []),
      {
        path: 'join',
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./join/join.component').then((m) => m.JoinComponent),
            canActivate: [
              authGuard(AuthGuardAccess.PUBLIC_AND_AUTHORIZED),
              joinGuard,
            ],
          },
          {
            path: 'success',
            loadComponent: () =>
              import('./join/join-success/join-success.component').then(
                (m) => m.JoinSuccessComponent,
              ),
            canActivate: [authGuard(AuthGuardAccess.PUBLIC_AND_AUTHORIZED)],
          },
        ],
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./forgot-password/forgot-password.component').then(
            (m) => m.ForgotPasswordComponent,
          ),
        canActivate: [authGuard(AuthGuardAccess.PUBLIC)],
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./reset-password/reset-password.component').then(
            (m) => m.ResetPasswordComponent,
          ),
        canActivate: [authGuard(AuthGuardAccess.PUBLIC), resetPasswordGuard],
      },
    ],
  },
] as const;
